import type { TFunction } from 'i18next';
import { z } from 'zod';
import { ISO_DATE_PATTERN } from '@/common/utils/dateFormats';
import type { FieldType } from './types';

export const TEXT_MAX_LENGTH = 1000;
export const MULTILINE_TEXT_MAX_LENGTH = 4000;
export const NOTE_MAX_LENGTH = 4000;
export const CURRENCY_MIN_VALUE = 0;
export const CURRENCY_MAX_VALUE = 9_999_999_999_999.99;
export const LINK_URL_MAX_LENGTH = 2048;
export const LINK_DESCRIPTION_MAX_LENGTH = 500;
export const MULTI_VALUE_MAX_COUNT = 100;
export const MULTI_TEXT_VALUE_MAX_LENGTH = 1000;

export interface ValidationResult {
  isValid: boolean;
  error?: string;
}

const linkValueSchema = (t: TFunction<'translation'>) =>
  z.object({
    url: z
      .string()
      .url(t(($) => $.adaptivePlanner.itemPanel.customFields.validation.linkUrlInvalid))
      .max(
        LINK_URL_MAX_LENGTH,
        t(($) => $.adaptivePlanner.itemPanel.customFields.validation.linkUrlMaxLength, {
          maxLength: LINK_URL_MAX_LENGTH,
        }),
      ),
    description: z
      .string()
      .max(
        LINK_DESCRIPTION_MAX_LENGTH,
        t(($) => $.adaptivePlanner.itemPanel.customFields.validation.linkDescriptionMaxLength, {
          maxLength: LINK_DESCRIPTION_MAX_LENGTH,
        }),
      )
      .optional(),
  });

export const createFieldValidationSchemas = (t: TFunction<'translation'>) => ({
  TEXT: z.string().max(
    TEXT_MAX_LENGTH,
    t(($) => $.adaptivePlanner.itemPanel.customFields.validation.textMaxLength, {
      maxLength: TEXT_MAX_LENGTH,
    }),
  ),

  MULTILINE_TEXT: z.string().max(
    MULTILINE_TEXT_MAX_LENGTH,
    t(($) => $.adaptivePlanner.itemPanel.customFields.validation.multilineTextMaxLength, {
      maxLength: MULTILINE_TEXT_MAX_LENGTH,
    }),
  ),

  NOTE: z.string().max(
    NOTE_MAX_LENGTH,
    t(($) => $.adaptivePlanner.itemPanel.customFields.validation.noteMaxLength, {
      maxLength: NOTE_MAX_LENGTH,
    }),
  ),

  NUMERIC: z
    .number()
    .finite(t(($) => $.adaptivePlanner.itemPanel.customFields.validation.numericInvalid)),

  CURRENCY: z
    .number()
    .min(
      CURRENCY_MIN_VALUE,
      t(($) => $.adaptivePlanner.itemPanel.customFields.validation.currencyMin, {
        minValue: CURRENCY_MIN_VALUE,
      }),
    )
    .max(
      CURRENCY_MAX_VALUE,
      t(($) => $.adaptivePlanner.itemPanel.customFields.validation.currencyMax, {
        maxValue: CURRENCY_MAX_VALUE,
      }),
    ),

  DATE: z.string().regex(
    ISO_DATE_PATTERN,
    t(($) => $.adaptivePlanner.itemPanel.customFields.validation.dateInvalid),
  ),

  LINK: linkValueSchema(t),

  MULTI_LINK: z.array(linkValueSchema(t)).max(
    MULTI_VALUE_MAX_COUNT,
    t(($) => $.adaptivePlanner.itemPanel.customFields.validation.multiLinkMaxCount, {
      maxCount: MULTI_VALUE_MAX_COUNT,
    }),
  ),

  CHECKBOX: z.boolean(),

  PICKLIST: z
    .number()
    .positive(t(($) => $.adaptivePlanner.itemPanel.customFields.validation.picklistRequired)),

  MULTI_PICKLIST: z.array(z.number().positive()).max(
    MULTI_VALUE_MAX_COUNT,
    t(($) => $.adaptivePlanner.itemPanel.customFields.validation.multiPicklistMaxCount, {
      maxCount: MULTI_VALUE_MAX_COUNT,
    }),
  ),

  USER: z.string().min(
    1,
    t(($) => $.adaptivePlanner.itemPanel.customFields.validation.userRequired),
  ),

  MULTI_USER: z.array(z.string().min(1)).max(
    MULTI_VALUE_MAX_COUNT,
    t(($) => $.adaptivePlanner.itemPanel.customFields.validation.multiUserMaxCount, {
      maxCount: MULTI_VALUE_MAX_COUNT,
    }),
  ),

  MULTI_CUSTOMER: z.array(z.number().positive()).max(
    MULTI_VALUE_MAX_COUNT,
    t(($) => $.adaptivePlanner.itemPanel.customFields.validation.multiValueMaxCountReached, {
      maxCount: MULTI_VALUE_MAX_COUNT,
    }),
  ),

  ITEM: z
    .number()
    .positive(t(($) => $.adaptivePlanner.itemPanel.customFields.validation.itemRequired)),

  MULTI_ITEM: z.array(z.number().positive()).max(
    MULTI_VALUE_MAX_COUNT,
    t(($) => $.adaptivePlanner.itemPanel.customFields.validation.multiItemMaxCount, {
      maxCount: MULTI_VALUE_MAX_COUNT,
    }),
  ),

  MULTI_TEXT: z
    .array(
      z.string().max(
        MULTI_TEXT_VALUE_MAX_LENGTH,
        t(($) => $.adaptivePlanner.itemPanel.customFields.validation.multiTextValueMaxLength, {
          maxLength: MULTI_TEXT_VALUE_MAX_LENGTH,
        }),
      ),
    )
    .max(
      MULTI_VALUE_MAX_COUNT,
      t(($) => $.adaptivePlanner.itemPanel.customFields.validation.multiTextMaxCount, {
        maxCount: MULTI_VALUE_MAX_COUNT,
      }),
    ),
});

export const validateFieldValue = (
  t: TFunction<'translation'>,
  fieldType: FieldType,
  value: unknown,
): ValidationResult => {
  try {
    const schemas = createFieldValidationSchemas(t);
    schemas[fieldType].parse(value);
    return { isValid: true };
  } catch (error) {
    if (error instanceof z.ZodError) {
      const firstIssue = error.issues[0];
      return {
        isValid: false,
        error:
          firstIssue?.message ??
          t(($) => $.adaptivePlanner.itemPanel.customFields.validation.invalidValue),
      };
    }
    return {
      isValid: false,
      error: t(($) => $.adaptivePlanner.itemPanel.customFields.validation.invalidValue),
    };
  }
};
